import { QueryResult } from 'react-query';
import moment from 'moment-timezone';
import { defaultSmDate } from 'utils/formatter';
import { callApi, Controller, getApiUrl, useApi, UseApiOptions } from './useApi';
// eslint-disable-next-line import/no-cycle
import { getKunde, Kunde } from './kunde';
import { addServicePosition, defaultServicePosition, ServicePosition } from './servicePosition';
import { getArtikelDetailKalkulationen } from './artikelDetailKalkulation';
// eslint-disable-next-line import/no-cycle
import { getArtikelDetail } from './artikelDetail';
import type { ArtikelCalculator } from '../hooks/useArtikelCalculator';
import type { Auftrag } from './auftrag';
import type { AuftragPosition } from './auftragPosition';
import type { Angebot } from './angebot';
import type { AngebotPosition } from './angebotPosition';
import type { Zusatzadresse } from './zusatzadresse';

export interface Service {
    kNr?: number;
    nr: string;
    auNr: string;
    kaNr: number;
    kaName: string;
    lieferAdrNr: number;
    status: number;
    typ: number;
    datum: string;
    betreuer: number;
    telBetreuer: number;
    txt: string;
    ara: number;
    liefBed: number;
    liefDat: string;
    liefZeit: number;
    liefKw: number;
    zahlBed: number;
    mwstCode: number;
    sNetto: number;
    sMwst: number;
    sBrutto: number;
    extDoku: boolean;
    aktText: string;
    zusatz: string;
    anlageDat?: string;
    anlageUhr?: number;
    anlageUser?: string;
    updDat?: string;
    updUhr?: number;
    updUser?: string;

    kunde?: Kunde;
    lieferAdr?: Zusatzadresse;
}

export function getDefaultService(kNr?: number): Service {
    const values: Service = {
        kNr: undefined,
        nr: '',
        auNr: '',
        kaNr: 0,
        kaName: '',
        lieferAdrNr: 0,
        status: 0,
        typ: 0,
        datum: defaultSmDate,
        betreuer: 0,
        telBetreuer: 0,
        txt: '',
        ara: 0,
        liefBed: 19,
        liefDat: defaultSmDate,
        liefZeit: 0,
        liefKw: 0,
        zahlBed: 1,
        mwstCode: 0,
        sNetto: 0,
        sMwst: 0,
        sBrutto: 0,
        extDoku: true,
        aktText: '',
        zusatz: '',
    };

    const now = moment();

    return {
        ...values,
        kNr,
        datum: now.format('YYYY-MM-DD') + 'T00:00:00',
        liefDat: now.format('YYYY-MM-DD') + 'T00:00:00',
    };
}

export interface ServiceFilter {
    nr?: string;
    nrSearch?: string;
    kNr?: number;
    auNr?: string;
    auNrSearch?: string;
    artikelGruppeNr?: number;
    artikelNr?: string;
    searchText?: string;
    betreuer?: number;
    betreuerExact?: number;
    telBetreuerExact?: number;
    datumVon?: string;
    datumBis?: string;
    datBisVon?: string;
    datBisBis?: string;
    kundePlzVon?: string;
    kundePlzBis?: string;
    kundeUmsatzVon?: number | null;
    kundeUmsatzBis?: number | null;
    kundeBranchen?: number[];
    kundeErweiterteSuchen?: number[];
    kundeInteresseFuerProdukte?: number[];
    kundeBetreuerExact?: number;
    kundeTelBetreuerExact?: number;
    produktgruppen?: number[];
    status?: number;
    statusList?: number[];
    typ?: number;
    typList?: number[];
    withKunde?: boolean;
    maxRows?: number;
    sortAsc?: boolean;
}

export function useService(filter: ServiceFilter, apiOptions?: UseApiOptions<Service>): QueryResult<Service> {
    return useApi<Service>(Controller.service + '/one', { filter }, apiOptions);
}

export function useServiceList(filter: ServiceFilter, apiOptions?: UseApiOptions<Service[]>): QueryResult<Service[]> {
    return useApi<Service[]>(Controller.service + '/list', { filter }, apiOptions);
}

interface AddServiceResult {
    newNr: string;
}

export async function addService(service: Service): Promise<AddServiceResult> {
    return callApi<AddServiceResult>(Controller.service + '/add', { service });
}

export async function updateService(service: Service): Promise<void> {
    return callApi<void>(Controller.service + '/update', { service });
}

export async function deleteService(service: Service): Promise<void> {
    return callApi<void>(Controller.service + '/delete', { service });
}

export async function sendService(
    service: Service,
    kaNrListTo: number[],
    kaNrListCC: number[],
    lastSigned: boolean,
): Promise<void> {
    return callApi<void>(Controller.service + '/send', { nr: service.nr, kaNrListTo, kaNrListCC, lastSigned });
}

export async function copyService(
    kNr: number,
    service: Service,
    servicePositionen?: ServicePosition[],
): Promise<AddServiceResult> {
    const newKundeList = await getKunde({ nr: kNr, withZusatzadresseList: true });

    if (newKundeList.length !== 1) {
        throw new Error('Kunde nicht gefunden');
    }

    const newKunde = newKundeList[0];

    let lieferAdrNr = 0;
    if (service.lieferAdrNr && newKunde.nr === service.kNr) {
        const lieferAdr = newKunde.zusatzadresseList?.find((za) => za.nr === service.lieferAdrNr);
        if (lieferAdr && !lieferAdr.archiv) {
            lieferAdrNr = lieferAdr.nr;
        }
    }

    const defaultService = getDefaultService();

    const newService = {
        ...service,
        kNr,
        status: 0,
        auNr: '',
        datum: defaultService.datum,
        betreuer: newKunde.betreuer || defaultService.betreuer,
        telBetreuer: newKunde.telBetreuer || defaultService.telBetreuer,
        lieferAdrNr,
    };

    const result = await addService(newService);

    if (servicePositionen) {
        // eslint-disable-next-line no-restricted-syntax
        for (const servicePosition of servicePositionen) {
            let ekPreis = servicePosition.posEkPreis;
            try {
                // eslint-disable-next-line no-await-in-loop
                const artikelDetail = await getArtikelDetail({
                    artikelGruppeNr: servicePosition.artikelGruppeNr,
                    artikelNr: servicePosition.artikelNr,
                    includeArchiv: true,
                });

                // eslint-disable-next-line no-await-in-loop
                const artikelDetailKalkulationen = await getArtikelDetailKalkulationen({
                    artikelGruppeNr: servicePosition.artikelGruppeNr,
                    artikelNr: servicePosition.artikelNr,
                });

                if (artikelDetail && artikelDetailKalkulationen) {
                    const defaultKalkulation = artikelDetailKalkulationen.find(
                        (k) => k.eh === artikelDetail.artikel?.einheit,
                    );

                    if (defaultKalkulation?.ek2) {
                        ekPreis = defaultKalkulation.ek2;
                    }
                }
            } catch (error) {
                console.error(error);
            }

            // eslint-disable-next-line no-await-in-loop
            await addServicePosition({ ...servicePosition, sNr: result.newNr, posEkPreis: ekPreis });
        }
    }

    return result;
}

interface RecalculateServiceResult {
    service: Service;
}

export async function recalculateService(service: Service): Promise<RecalculateServiceResult> {
    return callApi<RecalculateServiceResult>(Controller.service + '/recalculate', { service });
}

export async function signService(nr: string, signature: File): Promise<boolean> {
    const data = new FormData();
    data.append('nr', nr);
    data.append('signature', signature);

    const request: RequestInit = {
        method: 'post',
        mode: 'cors',
        credentials: 'include',
        headers: {},
        body: data,
    };

    const result = await fetch(getApiUrl(Controller.service + '/sign'), request);
    return result.status === 200;
}

export async function addServiceFromAuftrag(
    artikelCalculator: ArtikelCalculator,
    auftrag: Auftrag,
    auftragPositionen?: AuftragPosition[],
): Promise<AddServiceResult> {
    const defaultService = getDefaultService();

    const newService: Service = {
        ...defaultService,
        kNr: auftrag.kNr,
        auNr: auftrag.nr,
        betreuer: auftrag.betreuer,
        status: defaultService.status,
        datum: defaultService.datum,
        zahlBed: auftrag.zahlBed || defaultService.zahlBed,
        aktText: auftrag.aktText,
        mwstCode: auftrag.mwstCode,
    };

    const result = await addService(newService);

    if (auftragPositionen) {
        let pos = 1;
        // eslint-disable-next-line no-restricted-syntax
        for (const auftragPosition of auftragPositionen) {
            if (auftragPosition.transfer) {
                // eslint-disable-next-line no-await-in-loop
                /*
                const { ePreis, ekPreis } = await artikelCalculator.getPreise(
                    auftragPosition.artikelGruppeNr,
                    auftragPosition.artikelNr,
                    auftragPosition.posEinh,
                );
                 */

                const netto = (auftragPosition.transferMenge || 0) * auftragPosition.posEPreis;
                const mwstFactor = auftragPosition.posMwst ? 1 + auftragPosition?.posMwst / 100 : 1;
                const brutto = auftragPosition.posMwst ? netto * mwstFactor : netto;

                const newPosition: ServicePosition = {
                    ...defaultServicePosition,
                    sNr: result.newNr,
                    pos,
                    artikelGruppeNr: auftragPosition.artikelGruppeNr,
                    artikelNr: auftragPosition.artikelNr,
                    posText: auftragPosition.posText,
                    posEinh: auftragPosition.posEinh,
                    posMenge: auftragPosition.transferMenge || 0,
                    posEkPreis: auftragPosition.posEkPreis,
                    posEPreis: auftragPosition.posEPreis,
                    posNetto: netto,
                    posBrutto: brutto,
                    posMwst: auftragPosition.posMwst,
                    posBem: auftragPosition.posBem,
                };

                // eslint-disable-next-line no-await-in-loop
                await addServicePosition(newPosition);
                pos += 1;
            }
        }
    }

    return result;
}

export async function addServiceFromAngebot(
    artikelCalculator: ArtikelCalculator,
    angebot: Angebot,
    angebotPositionen?: AngebotPosition[],
): Promise<AddServiceResult> {
    const defaultService = getDefaultService();

    const newService: Service = {
        ...defaultService,
        kNr: angebot.kNr,
        auNr: angebot.nr,
        betreuer: angebot.betreuer,
        status: defaultService.status,
        datum: defaultService.datum,
        zahlBed: angebot.zahlBed || defaultService.zahlBed,
        aktText: angebot.aktText,
        mwstCode: angebot.mwstCode,
    };

    const result = await addService(newService);

    if (angebotPositionen) {
        let pos = 1;
        // eslint-disable-next-line no-restricted-syntax
        for (const angebotPosition of angebotPositionen) {
            if (angebotPosition.transfer) {
                // eslint-disable-next-line no-await-in-loop
                /*
                const { ePreis, ekPreis } = await artikelCalculator.getPreise(
                    angebotPosition.artikelGruppeNr,
                    angebotPosition.artikelNr,
                    angebotPosition.posEinh,
                );
                 */

                const netto = (angebotPosition.transferMenge || 0) * angebotPosition.posEPreis;
                const mwstFactor = angebotPosition.posMwst ? 1 + angebotPosition?.posMwst / 100 : 1;
                const brutto = angebotPosition.posMwst ? netto * mwstFactor : netto;

                const newPosition: ServicePosition = {
                    ...defaultServicePosition,
                    sNr: result.newNr,
                    pos,
                    artikelGruppeNr: angebotPosition.artikelGruppeNr,
                    artikelNr: angebotPosition.artikelNr,
                    posText: angebotPosition.posText,
                    posEinh: angebotPosition.posEinh,
                    posMenge: angebotPosition.transferMenge || 0,
                    posEkPreis: angebotPosition.posEkPreis,
                    posEPreis: angebotPosition.posEPreis,
                    posNetto: netto,
                    posBrutto: brutto,
                    posMwst: angebotPosition.posMwst,
                    posBem: angebotPosition.posBem,
                };

                // eslint-disable-next-line no-await-in-loop
                await addServicePosition(newPosition);
                pos += 1;
            }
        }
    }

    return result;
}
